import commonStyles from 'config/commonStyles';
import { truncateString } from 'helpers/helperFunctions';
import { images } from 'assets';
import { makeStyles, Theme, createStyles, alpha, Checkbox, Button } from '@material-ui/core';
import { Autocomplete, FilterOptionsState } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import clsx from 'clsx';
import i18n from 'i18n-js';
import { RestaurantGroup } from '../interfaces';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        ...commonStyles,
        listBox: {
            marginTop: 0,
            padding: 0,
        },
        paper: {
            marginTop: 0,
        },
        dropdownItem: {
            padding: 0,
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
        },
        selectedDropdownItem: {
            backgroundColor: alpha(theme.palette.primary.main, 0.16),
        },
        popper: {
            position: 'fixed',
        },
        textEllipsis: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            paddingRight: 10,
        },
        actionsContainer: {
            display: 'flex',
            gap: 8,
            padding: '12px 12px',
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            justifyContent: 'space-between',
        },
        groupItem: {
            display: 'flex',
            alignItems: 'center',
            background: theme.palette.grey[200],
            cursor: 'pointer',
        },
    })
);

export interface MultiSelectAutocompleteProps {
    options: RestaurantGroup[];
    inputLabel: string;
    inputPlaceholder?: string;
    selectedValues?: RestaurantGroup[];
    onChange?: (event: React.ChangeEvent<{}>, values: RestaurantGroup[]) => void;
    getOptionLabel: (option: RestaurantGroup) => string;
    getOptionSelected?: (option: RestaurantGroup, value: RestaurantGroup) => boolean;
    style?: React.CSSProperties;
    popupIcon?: string;
    trimLength?: number;
    error?: React.ReactNode | boolean;
    name?: string;
    autoCompleteProps?: Partial<React.ComponentProps<typeof Autocomplete>>;
    textFieldProps?: Partial<React.ComponentProps<typeof TextField>>;
}

const ListboxComponent = React.forwardRef<HTMLUListElement, any>(function ListboxComponent(
    props,
    ref
) {
    const classes = useStyles();
    const { children, selectAll, clearAll, listboxRef, ...other } = props;

    return (
        <ul
            {...other}
            ref={(node) => {
                // ✅ MUI internal ref
                if (typeof ref === 'function') ref(node);
                else if (ref)
                    (ref as React.MutableRefObject<HTMLUListElement | null>).current = node;

                // ✅ your scroll ref
                if (listboxRef) listboxRef.current = node;
            }}
        >
            <li className={classes.actionsContainer}>
                <Button variant="contained" color="primary" size="small" onClick={selectAll}>
                    {i18n.t('common.selectAll')}
                </Button>

                <Button variant="outlined" color="primary" size="small" onClick={clearAll}>
                    {i18n.t('orders.clear')}
                </Button>
            </li>

            {children}
        </ul>
    );
});

function StockRestaurantSelect({
    options,
    inputLabel,
    inputPlaceholder,
    selectedValues = [],
    onChange,
    getOptionLabel,
    getOptionSelected,
    style,
    popupIcon = images.icons.filter,
    trimLength = 25,
    error,
    name = 'multiselect',
    autoCompleteProps,
    textFieldProps,
}: MultiSelectAutocompleteProps) {
    const classes = useStyles();
    const listboxRef = React.useRef<HTMLUListElement | null>(null);
    const scrollTopRef = React.useRef(0);

    const filterOptions = (
        options: RestaurantGroup[],
        state: FilterOptionsState<RestaurantGroup>
    ) => {
        const input = state.inputValue.toLowerCase();

        return options.filter((option) => {
            const nameMatch = option.name.toLowerCase().includes(input);
            const groupMatch = (option.groupName ?? '').toLowerCase().includes(input);

            return nameMatch || groupMatch;
        });
    };

    function getSimpleText() {
        return selectedValues.map(getOptionLabel).join(' / ');
    }

    function getLabelText() {
        if (error) {
            return error;
        }
        if (selectedValues.length > 0) {
            return truncateString(getSimpleText(), trimLength);
        }
        return inputLabel;
    }

    function selectAll() {
        scrollTopRef.current = 0;
        onChange?.({} as any, options);
    }

    function clearAll() {
        scrollTopRef.current = 0;
        onChange?.({} as any, []);
    }

    const { groupMap, grouped } = useMemo(() => {
        const groupMap = new Map<string, string>();
        const grouped = new Map<string, RestaurantGroup[]>();

        options.forEach((o) => {
            const key = o.groupId;

            groupMap.set(key, o.groupName);

            if (!grouped.has(key)) {
                grouped.set(key, []);
            }

            grouped.get(key)!.push(o);
        });

        return { groupMap, grouped };
    }, [options]);

    const selectedIds = useMemo(() => {
        return new Set(selectedValues.map((s) => s.id));
    }, [selectedValues]);

    const isGroupFullySelected = useCallback(
        (groupId: string) => {
            const items = grouped.get(groupId) ?? [];
            return items.every((i) => selectedIds.has(i.id));
        },
        [grouped, selectedIds]
    );

    const isGroupPartiallySelected = useCallback(
        (groupId: string) => {
            const items = grouped.get(groupId) ?? [];

            let selectedCount = 0;
            for (const i of items) {
                if (selectedIds.has(i.id)) selectedCount++;
            }

            return selectedCount > 0 && selectedCount < items.length;
        },
        [grouped, selectedIds]
    );

    const toggleGroup = useCallback(
        (groupId: string) => {
            const items = grouped.get(groupId) ?? [];
            const allSelected = items.every((i) => selectedIds.has(i.id));
            let newValues;

            if (allSelected) {
                newValues = selectedValues.filter((s) => !items.some((i) => i.id === s.id));
            } else {
                const merged = [...selectedValues, ...items];
                newValues = Array.from(new Map(merged.map((i) => [i.id, i])).values());
            }

            if (listboxRef.current) {
                scrollTopRef.current = listboxRef.current.scrollTop;
            }

            onChange?.({} as any, newValues);
        },
        [grouped, selectedIds, selectedValues, onChange]
    );

    const renderGroup = useCallback(
        (params) => {
            const groupId = params.group;
            const groupName = groupMap.get(groupId) ?? 'Ungrouped';

            const checked = isGroupFullySelected(groupId);
            const indeterminate = isGroupPartiallySelected(groupId);

            return (
                <div key={params.key}>
                    <div className={classes.groupItem} onClick={() => toggleGroup(groupId)}>
                        <Checkbox checked={checked} indeterminate={indeterminate} />
                        {groupName}
                    </div>

                    <div>{params.children}</div>
                </div>
            );
        },
        [groupMap, isGroupFullySelected, isGroupPartiallySelected, toggleGroup]
    );

    const handleChange = useCallback(
        (event, values) => {
            if (listboxRef.current) {
                scrollTopRef.current = listboxRef.current.scrollTop;
            }

            onChange?.(event, values);
        },
        [onChange]
    );

    useLayoutEffect(() => {
        if (listboxRef.current) {
            listboxRef.current.scrollTop = scrollTopRef.current;
        }
    }, [selectedValues]);

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            value={selectedValues}
            getOptionLabel={getOptionLabel as (option: unknown) => string}
            getOptionSelected={
                getOptionSelected as ((option: unknown, value: unknown) => boolean) | undefined
            }
            style={style}
            popupIcon={
                <img
                    src={popupIcon}
                    alt="filter-icon"
                    style={{
                        width: popupIcon === images.icons.filter ? 18 : 'auto',
                        marginRight: 5,
                    }}
                />
            }
            onChange={handleChange}
            classes={{
                option: classes.dropdownItem,
                listbox: classes.listBox,
                paper: classes.paper,
                popper: popupIcon === images.icons.filter ? undefined : classes.popper,
            }}
            renderTags={() => null}
            ListboxComponent={(props) => (
                <ListboxComponent
                    selectAll={selectAll}
                    clearAll={clearAll}
                    {...props}
                    listboxRef={listboxRef}
                />
            )}
            renderOption={(opt, { selected }) => {
                const option = opt as RestaurantGroup;
                return (
                    <div
                        className={clsx(
                            classes.flexRowSpaceBetween,
                            classes.width100,
                            classes.bottomLine,
                            {
                                [classes.selectedDropdownItem]: selected,
                            }
                        )}
                        style={{ paddingLeft: 15 }}
                    >
                        <div className={clsx(classes.flexRow, classes.width100)}>
                            <Checkbox
                                color="primary"
                                checked={selected}
                                style={{ marginRight: 8 }}
                            />
                            <div className={classes.textEllipsis}>{option?.name ?? ''}</div>
                        </div>
                    </div>
                );
            }}
            groupBy={(option: any) => option?.groupId}
            renderGroup={renderGroup}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    name={name}
                    label={getLabelText()}
                    placeholder={inputPlaceholder}
                    variant="filled"
                    error={!!error}
                    InputProps={{
                        ...params.InputProps,
                        ...textFieldProps?.InputProps,
                        style: {
                            opacity: popupIcon === images.icons.filter ? 0.6 : 1,
                            cursor: 'pointer',
                            ...textFieldProps?.InputProps?.style,
                        },
                    }}
                />
            )}
            {...autoCompleteProps}
            // @ts-ignore
            filterOptions={filterOptions}
        />
    );
}

export default StockRestaurantSelect;
